/** Pure keyboard vocabulary shared by the terminal and browser; no runtime or platform imports. */
export const LEADER_TIMEOUT_MS = 2000;

/** Display/listing order for /keybinds. */
export const KEYBIND_ACTIONS = [
  'leader', 'quit', 'mode_toggle', 'reasoning_cycle', 'stash', 'rewind', 'rename', 'subagent_cycle', 'subagent_background', 'subagent_stop', 'telemetry_toggle',
  'paste_image', 'queue_steer', 'queue_remove', 'stop', 'reconnect', 'help', 'theme_picker', 'model_picker', 'sessions_picker',
  'react_positive', 'react_negative', 'reply_quote', 'search',
];

/** Default chord spec per action. Spec grammar (opencode-inspired):
 *  - comma-separated alternatives: `"shift+tab,ctrl+tab"`
 *  - a direct chord: modifiers `ctrl`/`shift`/`alt`/`super` + a base key (`ctrl+r`, `f2`, `pageup`)
 *  - a leader sequence: `"leader t"` — press the leader chord, then the key
 *  - `"none"` unbinds the action. */
export const DEFAULT_KEYBINDS = {
  leader: 'ctrl+x',
  quit: 'ctrl+c,ctrl+z',
  mode_toggle: 'shift+tab,ctrl+tab',
  reasoning_cycle: 'ctrl+r',
  paste_image: 'ctrl+v',
  stash: 'ctrl+s',
  rewind: 'f9',
  rename: 'f2',
  subagent_cycle: 'ctrl+o',
  subagent_background: 'ctrl+b',
  // A stop is a destructive act, so it rides the leader like the conversation's own stop: one
  // deliberate press, never a chord the hand lands on while typing.
  subagent_stop: 'leader k',
  telemetry_toggle: 'ctrl+p',
  // Sending a queued message now is the queue's own Enter: the same key that sends the draft in the
  // composer, one leader press away from the message it actually delivers.
  queue_steer: 'leader enter',
  queue_remove: 'leader x',
  stop: 'leader s',
  reconnect: 'leader r',
  help: 'leader h',
  theme_picker: 'leader t',
  model_picker: 'leader m',
  sessions_picker: 'leader l',
  react_positive: 'leader 👍',
  react_negative: 'leader 👎',
  reply_quote: 'leader q',
  search: 'ctrl+f',
};

/** The stock chord spec for an action — used by the interactive editor to prune overrides that were
 *  rebound back to their default (so the persisted map only ever holds genuine customizations). */
export function keybindDefault(action, windows = false) {
  return action === 'paste_image' && windows ? 'alt+v' : DEFAULT_KEYBINDS[action];
}

export const MODIFIER_NAMES = ['ctrl', 'shift', 'alt', 'super'];
const SPECIAL_BASES = new Set([
  'escape', 'esc', 'enter', 'return', 'tab', 'space', 'backspace', 'delete', 'insert', 'clear',
  'home', 'end', 'pageup', 'pagedown', 'up', 'down', 'left', 'right',
  'f1', 'f2', 'f3', 'f4', 'f5', 'f6', 'f7', 'f8', 'f9', 'f10', 'f11', 'f12',
]);
const SYMBOL_BASES = new Set([...'`-=[]\\;\',./!@#$%^&*()_|~{}:<>?']);
/** Bare (or shift-only) bases that are safe as DIRECT bindings — keys that never type text and are
 *  not structural editor keys. Everything else needs ctrl/alt/super, or must ride behind the leader. */
const BARE_DIRECT_BASES = new Set(['f1', 'f2', 'f3', 'f4', 'f5', 'f6', 'f7', 'f8', 'f9', 'f10', 'f11', 'f12', 'pageup', 'pagedown', 'insert']);

function parseChord(raw) {
  const parts = raw.split('+').map((p) => p.trim().toLowerCase());
  if (parts.length === 0 || parts.some((p) => !p)) return null;
  const base = parts[parts.length - 1];
  const mods = parts.slice(0, -1);
  if (mods.some((m) => !MODIFIER_NAMES.includes(m))) return null;
  if (new Set(mods).size !== mods.length) return null;
  if (!/^[a-z0-9]$/.test(base) && !SPECIAL_BASES.has(base) && !SYMBOL_BASES.has(base)) return null;
  // Canonical modifier order so the id doubles as the display label.
  const ordered = MODIFIER_NAMES.filter((m) => mods.includes(m));
  return { mods: ordered, base, id: [...ordered, base].join('+') };
}

/** A direct (non-leader) binding must not shadow typing or structural keys: it needs a real modifier
 *  (ctrl/alt/super), a non-typing base (f-keys, page keys, insert) — or the classic shift+tab. */
function directBindable(chord) {
  if (chord.mods.some((m) => m === 'ctrl' || m === 'alt' || m === 'super')) return true;
  if (BARE_DIRECT_BASES.has(chord.base)) return true;
  return chord.mods.length === 1 && chord.mods[0] === 'shift' && chord.base === 'tab';
}

/** Parse one action's chord spec (see DEFAULT_KEYBINDS grammar). `forLeader` restricts the spec to
 *  direct chords — the leader itself cannot be a leader sequence. */
export function parseKeybind(spec, opts = {}) {
  const trimmed = spec.trim();
  if (trimmed.toLowerCase() === 'none') return { ok: true, bindings: [] };
  if (!trimmed) return { ok: false, error: 'empty chord' };
  const bindings = [];
  for (const entry of trimmed.split(',')) {
    const tokens = entry.trim().split(/\s+/);
    if (tokens.length === 2 && tokens[0].toLowerCase() === 'leader') {
      if (opts.forLeader) return { ok: false, error: 'the leader itself cannot be a leader sequence' };
      const chord = tokens[1] === '👍' || tokens[1] === '👎'
        ? { mods: [], base: tokens[1], id: tokens[1] }
        : parseChord(tokens[1]);
      if (!chord) return { ok: false, error: `unknown chord "${tokens[1]}"` };
      if (chord.base === 'escape' || chord.base === 'esc') return { ok: false, error: 'esc cancels the leader — it cannot follow it' };
      bindings.push({ leader: true, chord });
      continue;
    }
    if (tokens.length !== 1) return { ok: false, error: `unknown binding "${entry.trim()}"` };
    const chord = parseChord(tokens[0]);
    if (!chord) return { ok: false, error: `unknown chord "${tokens[0]}"` };
    if (!directBindable(chord)) return { ok: false, error: `"${chord.id}" would shadow typing — use a ctrl/alt chord or "leader ${chord.id}"` };
    bindings.push({ leader: false, chord });
  }
  return { ok: true, bindings };
}

/** Ordered projections of the shared vocabulary; ordering also determines collision winners. */
export const CLI_KEYBIND_ACTIONS = KEYBIND_ACTIONS.filter((action) => action !== 'search');
export const WEB_KEYBIND_ACTIONS = KEYBIND_ACTIONS.filter((action) =>
  !['quit', 'paste_image', 'subagent_background', 'react_positive', 'react_negative', 'reply_quote'].includes(action));

const WEB_RESERVED = new Set(['ctrl+w', 'ctrl+t', 'ctrl+n', 'alt+f4', 'ctrl+alt+delete']);
const canonical = (bindings) => bindings.length
  ? [...new Set(bindings.map((binding) => `${binding.leader ? 'leader ' : ''}${binding.chord.id}`))].join(',')
  : 'none';

/** Pure merge and collision evaluation. CLI callers may show issues as warnings; web must reject them. */
export function resolveChatKeybinds(overrides = {}, { surface, windows = false }) {
  if (surface !== 'cli' && surface !== 'web') throw new Error('unknown keyboard surface');
  const actions = surface === 'cli' ? CLI_KEYBIND_ACTIONS : WEB_KEYBIND_ACTIONS;
  const bindings = new Map();
  const custom = new Set();
  const canonicalOverrides = {};
  const issues = [];
  for (const action of actions) {
    const parsed = parseKeybind(keybindDefault(action, windows && surface === 'cli'), { forLeader: action === 'leader' });
    if (!parsed.ok) throw new Error(`Invalid default for ${action}: ${parsed.error}`);
    bindings.set(action, parsed.bindings);
  }
  for (const [action, value] of Object.entries(overrides)) {
    if (!actions.includes(action)) {
      issues.push({ code: 'unknown_action', action, message: `unknown action "${action}" — ignored` });
      continue;
    }
    if (typeof value !== 'string') {
      issues.push({ code: 'invalid_type', action, message: `${action}: chord must be a string — default kept` });
      continue;
    }
    const parsed = parseKeybind(value, { forLeader: action === 'leader' });
    if (!parsed.ok) {
      issues.push({ code: 'invalid_syntax', action, message: `${action}: ${parsed.error} — default kept` });
      continue;
    }
    const reserved = surface === 'web' && parsed.bindings.find(({ chord }) =>
      chord.mods.includes('super') || WEB_RESERVED.has(chord.id));
    if (reserved) {
      issues.push({ code: 'reserved', action, chord: reserved.chord.id, message: `${action}: "${reserved.chord.id}" is reserved — default kept` });
      continue;
    }
    bindings.set(action, parsed.bindings);
    custom.add(action);
    const normalized = canonical(parsed.bindings);
    const defaultParsed = parseKeybind(keybindDefault(action, windows && surface === 'cli'), { forLeader: action === 'leader' });
    if (!defaultParsed.ok) throw new Error(`Invalid default for ${action}: ${defaultParsed.error}`);
    if (normalized !== canonical(defaultParsed.bindings)) canonicalOverrides[action] = normalized;
  }
  // A user's explicit chord owns that key over every other action's stock default. Keep alternative
  // defaults on the displaced action; explicit-versus-explicit collisions still reach validation below.
  const claimed = new Set([...custom].flatMap(action => bindings.get(action).map(binding =>
    `${binding.leader ? 'leader ' : ''}${binding.chord.id}`)));
  for (const action of actions) {
    if (!custom.has(action)) bindings.set(action, bindings.get(action).filter(binding =>
      !claimed.has(`${binding.leader ? 'leader ' : ''}${binding.chord.id}`)));
  }
  const directOwner = new Map();
  const leaderOwner = new Map();
  for (const action of actions) {
    for (const binding of bindings.get(action)) {
      const owners = binding.leader ? leaderOwner : directOwner;
      const owner = owners.get(binding.chord.id);
      if (owner === undefined) { owners.set(binding.chord.id, action); continue; }
      if (owner === action) continue;
      const chord = `${binding.leader ? 'leader ' : ''}${binding.chord.id}`;
      issues.push({ code: 'collision', action, owner, chord, message: `${action}: "${chord}" already bound to ${owner} — unreachable` });
    }
  }
  return { surface, actions, bindings, custom, canonicalOverrides, issues };
}

/** Strict validation shares all policy with the effective-map resolver. */
export function validateChatKeybinds(overrides, options) {
  const result = resolveChatKeybinds(overrides, options);
  return result.issues.length ? { ok: false, issues: result.issues } : { ok: true, value: result.canonicalOverrides };
}
